import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../utils/api';
import SearchableSelect from '../components/SearchableSelect';

const DEFAULT_COLORS = ['#4A90D9','#E74C3C','#27AE60','#F39C12','#8E44AD','#16A085','#E67E22','#2C3E50','#C0392B','#1ABC9C'];

function fmt(n) {
  return '₹' + parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0 });
}

// ── Left panel: catalogue tree picker ─────────────────────────────
function CataloguePicker({ tree, selectedClient, nodes, onAssign }) {
  const [catId, setCatId] = useState('');
  const [subId, setSubId] = useState('');
  const [itemId, setItemId] = useState('');
  const [nodeId, setNodeId] = useState('');
  const [price, setPrice] = useState('');
  const [gst, setGst] = useState('18');
  const [customMode, setCustomMode] = useState(false);
  const [customName, setCustomName] = useState('');
  const [catSearch, setCatSearch] = useState('');

  const selectedCat = tree.find(c => c.id === catId);
  const selectedSub = selectedCat?.subcategories?.find(s => s.id === subId);

  const filteredTree = useMemo(() => {
    if (!catSearch.trim()) return tree;
    const q = catSearch.toLowerCase();
    return tree.filter(cat =>
      cat.name.toLowerCase().includes(q) ||
      cat.subcategories?.some(s => s.name.toLowerCase().includes(q) ||
        s.items?.some(i => i.name.toLowerCase().includes(q)))
    );
  }, [tree, catSearch]);

  function handleAssign() {
    if (!selectedClient) return toast.error('Select a client first');
    if (!customMode && !itemId) return toast.error('Select a work item');
    if (customMode && !customName.trim()) return toast.error('Enter a custom service name');
    onAssign({
      catalogue_item_id: customMode ? null : itemId,
      custom_name: customMode ? customName.trim() : null,
      hierarchy_node_id: nodeId || null,
      price: parseFloat(price) || 0,
      gst_percent: parseFloat(gst) || 18,
    });
    setItemId(''); setSubId(''); setPrice(''); setCustomName('');
  }

  return (
    <div className="card" style={{ padding: '20px 20px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 14 }}>ADD SERVICE TO CLIENT</div>

      {/* Category search */}
      <div className="field" style={{ marginBottom: 10 }}>
        <label>Search catalogue</label>
        <input value={catSearch} onChange={e => setCatSearch(e.target.value)} placeholder="Search category, subcategory or item…" />
      </div>

      {!customMode ? (
        <>
          {/* Step 1: Category */}
          <div className="field">
            <label>1 · Category</label>
            <select value={catId} onChange={e => { setCatId(e.target.value); setSubId(''); setItemId(''); }}>
              <option value="">— Select category —</option>
              {filteredTree.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
            </select>
          </div>

          {/* Step 2: Subcategory */}
          {catId && (
            <div className="field">
              <label>2 · Subcategory</label>
              <select value={subId} onChange={e => { setSubId(e.target.value); setItemId(''); }}>
                <option value="">— Select subcategory —</option>
                {selectedCat?.subcategories?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}

          {/* Step 3: Work item */}
          {subId && (
            <div className="field">
              <label>3 · Work item</label>
              <select value={itemId} onChange={e => setItemId(e.target.value)}>
                <option value="">— Select item —</option>
                {selectedSub?.items?.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </div>
          )}
        </>
      ) : (
        <div className="field">
          <label>Custom service name</label>
          <input value={customName} onChange={e => setCustomName(e.target.value)} placeholder="e.g. Special Campaign Package" autoFocus />
        </div>
      )}

      <div style={{ marginBottom: 10 }}>
        <button className="btn-ghost btn-sm" onClick={() => { setCustomMode(v => !v); setItemId(''); setCustomName(''); }}>
          {customMode ? '← Back to catalogue' : '+ Add custom service'}
        </button>
      </div>

      {/* Assign to: client or person/stakeholder */}
      {nodes.length > 0 && (
        <div className="field">
          <label>Assign to (optional)</label>
          <select value={nodeId} onChange={e => setNodeId(e.target.value)}>
            <option value="">Client level (default)</option>
            {nodes.map(n => <option key={n.id} value={n.id}>{n.label}: {n.name}{n.parent_name ? ` (under ${n.parent_name})` : ''}</option>)}
          </select>
        </div>
      )}

      {/* Price */}
      <div className="form-grid">
        <div className="field">
          <label>Client price (₹)</label>
          <input type="number" value={price} onChange={e => setPrice(e.target.value)} placeholder="0" />
        </div>
        <div className="field">
          <label>GST %</label>
          <input type="number" value={gst} onChange={e => setGst(e.target.value)} />
        </div>
      </div>

      <button className="btn-primary" style={{ width: '100%' }} onClick={handleAssign}
        disabled={!selectedClient}>
        {selectedClient ? 'Assign to client' : 'Select a client first'}
      </button>
    </div>
  );
}

// ── Client card ────────────────────────────────────────────────────
function ClientCard({ client, assignments, nodes, onDelete, onUpdatePrice, onColorChange, searchQ }) {
  const [editingId, setEditingId] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [editColor, setEditColor] = useState(client.color || '#4A90D9');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState({});

  const accentColor = client.color || '#4A90D9';

  // Group: client-level vs node-level
  const clientLevel = assignments.filter(a => !a.hierarchy_node_id);
  const nodeLevel = assignments.filter(a => a.hierarchy_node_id);

  // Group node assignments by node_id
  const byNode = {};
  nodeLevel.forEach(a => {
    if (!byNode[a.hierarchy_node_id]) byNode[a.hierarchy_node_id] = [];
    byNode[a.hierarchy_node_id].push(a);
  });

  // Filter by search
  const filterAssignments = list => {
    if (!searchQ) return list;
    const q = searchQ.toLowerCase();
    return list.filter(a => (a.item_name || a.custom_name || '').toLowerCase().includes(q) ||
      (a.category_name || '').toLowerCase().includes(q) || (a.subcategory_name || '').toLowerCase().includes(q));
  };

  function AssignmentRow({ a }) {
    const label = a.item_name || a.custom_name || '—';
    const sub = a.category_name ? `${a.category_icon || ''} ${a.category_name} › ${a.subcategory_name}` : 'Custom';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 500 }}>{label}</div>
          <div style={{ fontSize: 10, color: 'var(--text3)' }}>{sub}</div>
        </div>
        {editingId === a.id ? (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <input type="number" value={editPrice} onChange={e => setEditPrice(e.target.value)}
              style={{ width: 80, padding: '2px 6px', borderRadius: 4, border: '1px solid var(--border)', fontSize: 12 }} />
            <button className="btn-primary btn-sm" onClick={() => { onUpdatePrice(a.id, editPrice); setEditingId(null); }}>✓</button>
            <button className="btn-ghost btn-sm" onClick={() => setEditingId(null)}>✕</button>
          </div>
        ) : (
          <>
            <span style={{ fontWeight: 600, color: accentColor, minWidth: 60, textAlign: 'right' }}>{fmt(a.price)}</span>
            <span style={{ fontSize: 10, color: 'var(--text3)' }}>{a.gst_percent}%</span>
            <button className="btn-sm" style={{ padding: '2px 6px' }} onClick={() => { setEditingId(a.id); setEditPrice(a.price); }}>✎</button>
            <button className="btn-danger btn-sm" style={{ padding: '2px 6px' }} onClick={() => onDelete(a.id)}>✕</button>
          </>
        )}
      </div>
    );
  }

  const filteredClientLevel = filterAssignments(clientLevel);
  const hasContent = filteredClientLevel.length > 0 || Object.keys(byNode).some(nid => filterAssignments(byNode[nid]).length > 0);
  if (searchQ && !hasContent && !client.name.toLowerCase().includes(searchQ.toLowerCase())) return null;

  return (
    <div className="card" style={{ marginBottom: 16, borderTop: `4px solid ${accentColor}`, padding: 0, overflow: 'hidden' }}>
      {/* Card header */}
      <div style={{ padding: '14px 16px', background: accentColor + '18', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Colour dot + picker */}
          <div style={{ position: 'relative' }}>
            <div onClick={() => setShowColorPicker(v => !v)}
              style={{ width: 22, height: 22, borderRadius: '50%', background: accentColor, cursor: 'pointer', border: '2px solid #fff', boxShadow: '0 1px 4px rgba(0,0,0,0.2)', flexShrink: 0 }} />
            {showColorPicker && (
              <div style={{ position: 'absolute', top: 28, left: 0, zIndex: 100, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', width: 200 }}>
                <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 6 }}>QUICK COLOURS</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {DEFAULT_COLORS.map(c => (
                    <div key={c} onClick={() => { setEditColor(c); onColorChange(client.id, c); setShowColorPicker(false); }}
                      style={{ width: 20, height: 20, borderRadius: '50%', background: c, cursor: 'pointer', border: editColor === c ? '2px solid #000' : '2px solid transparent' }} />
                  ))}
                </div>
                <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 4 }}>CUSTOM HEX</div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <input value={editColor} onChange={e => setEditColor(e.target.value)}
                    style={{ flex: 1, padding: '3px 6px', borderRadius: 4, border: '1px solid var(--border)', fontSize: 12, fontFamily: 'monospace' }}
                    placeholder="#4A90D9" maxLength={7} />
                  <button className="btn-primary btn-sm" onClick={() => { onColorChange(client.id, editColor); setShowColorPicker(false); }}>✓</button>
                </div>
              </div>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text1)' }}>{client.name}</div>
            <div style={{ fontSize: 10, color: 'var(--text3)' }}>{assignments.length} service{assignments.length !== 1 ? 's' : ''} assigned</div>
          </div>
        </div>
      </div>

      <div style={{ padding: '12px 16px' }}>
        {/* Client-level assignments */}
        {filteredClientLevel.length > 0 && (
          <div style={{ marginBottom: byNode && Object.keys(byNode).length > 0 ? 12 : 0 }}>
            {filteredClientLevel.map(a => <AssignmentRow key={a.id} a={a} />)}
          </div>
        )}
        {filteredClientLevel.length === 0 && Object.keys(byNode).length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text3)', fontStyle: 'italic', padding: '4px 0' }}>No services assigned yet</div>
        )}

        {/* Node-level sub-sections */}
        {Object.entries(byNode).map(([nid, list]) => {
          const node = nodes.find(n => n.id === nid);
          const filtered = filterAssignments(list);
          if (searchQ && !filtered.length) return null;
          const isExpanded = expandedNodes[nid] !== false;
          return (
            <div key={nid} style={{ marginTop: 10, border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
              <div onClick={() => setExpandedNodes(v => ({ ...v, [nid]: !isExpanded }))}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', background: 'var(--surface2)', cursor: 'pointer', fontSize: 12 }}>
                <span style={{ fontWeight: 600 }}>
                  <span style={{ fontSize: 10, color: 'var(--text3)', marginRight: 4 }}>{node?.label?.toUpperCase()}</span>
                  {node?.name || 'Unknown'}
                </span>
                <span style={{ display: 'inline-block', transition: 'transform 0.25s', transform: isExpanded ? 'rotate(180deg)' : 'none', fontSize: 10 }}>▼</span>
              </div>
              {isExpanded && (
                <div style={{ padding: '4px 10px' }}>
                  {filtered.map(a => <AssignmentRow key={a.id} a={a} />)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────
export default function CatalogueSettingsPage() {
  const qc = useQueryClient();
  const [selectedClientId, setSelectedClientId] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [catSearch, setCatSearch] = useState('');

  const { data: tree = [] } = useQuery({ queryKey: ['catalogue-tree'], queryFn: () => api.get('/catalogue').then(r => r.data), staleTime: 300000 });
  const { data: clients = [] } = useQuery({ queryKey: ['clients'], queryFn: () => api.get('/clients').then(r => r.data) });
  const { data: nodes = [] } = useQuery({
    queryKey: ['hierarchy-nodes', selectedClientId],
    queryFn: () => api.get(`/clients/${selectedClientId}/nodes`).then(r => r.data),
    enabled: !!selectedClientId,
  });

  // Fetch assignments for ALL clients that have any
  const { data: allAssignments = [] } = useQuery({
    queryKey: ['all-catalogue-assignments', clients.map(c => c.id).join(',')],
    queryFn: async () => {
      if (!clients.length) return [];
      const results = await Promise.all(
        clients.map(c => api.get(`/catalogue/assignments?client_id=${c.id}`).then(r => r.data).catch(() => []))
      );
      return results.flat();
    },
    enabled: clients.length > 0,
  });

  const assignMutation = useMutation({
    mutationFn: data => api.post('/catalogue/assignments', { ...data, client_id: selectedClientId }),
    onSuccess: () => { toast.success('Service assigned'); qc.invalidateQueries(['all-catalogue-assignments']); },
    onError: err => toast.error(err.response?.data?.error || 'Failed to assign'),
  });

  const deleteMutation = useMutation({
    mutationFn: id => api.delete(`/catalogue/assignments/${id}`),
    onSuccess: () => { toast.success('Removed'); qc.invalidateQueries(['all-catalogue-assignments']); },
    onError: () => toast.error('Failed to remove'),
  });

  const updatePriceMutation = useMutation({
    mutationFn: ({ id, price }) => api.patch(`/catalogue/assignments/${id}`, { price: parseFloat(price) }),
    onSuccess: () => { toast.success('Price updated'); qc.invalidateQueries(['all-catalogue-assignments']); },
    onError: () => toast.error('Failed to update price'),
  });

  const colorMutation = useMutation({
    mutationFn: ({ clientId, color }) => api.patch(`/catalogue/clients/${clientId}/color`, { color }),
    onSuccess: () => { qc.invalidateQueries(['clients']); },
    onError: () => toast.error('Failed to save colour'),
  });

  // Enrich nodes with parent name for display
  const enrichedNodes = useMemo(() => {
    const map = {};
    nodes.forEach(n => map[n.id] = n);
    return nodes.map(n => ({ ...n, parent_name: n.parent_id ? map[n.parent_id]?.name : null }));
  }, [nodes]);

  // Filter clients for card display
  const filteredClients = useMemo(() => {
    const q = clientSearch.toLowerCase();
    return clients.filter(c => !q || c.name.toLowerCase().includes(q));
  }, [clients, clientSearch]);

  // Clients that actually have assignments
  const clientsWithAssignments = filteredClients.filter(c =>
    allAssignments.some(a => a.client_id === c.id)
  );

  return (
    <div>
    <h2 style={{ marginBottom: 20 }}>📋 Master Service Catalogue</h2>
    <div style={{ display: 'grid', gridTemplateColumns: '380px 1fr', gap: 20, alignItems: 'start' }}>

      {/* ── LEFT PANEL ── */}
      <div>
        {/* Client selector */}
        <div className="card" style={{ marginBottom: 16, padding: '16px 20px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 10 }}>SELECT CLIENT</div>
          <SearchableSelect value={selectedClientId} onChange={setSelectedClientId}
            items={clients} placeholder="— Choose a client —" />
        </div>

        {/* Catalogue picker */}
        <CataloguePicker
          tree={tree}
          selectedClient={selectedClientId}
          nodes={enrichedNodes}
          onAssign={data => assignMutation.mutate(data)}
        />
      </div>

      {/* ── RIGHT PANEL — Client cards ── */}
      <div>
        {/* Search */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input value={clientSearch} onChange={e => setClientSearch(e.target.value)}
            placeholder="🔍 Search clients…" style={{ flex: 1, padding: '7px 12px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13 }} />
          <input value={catSearch} onChange={e => setCatSearch(e.target.value)}
            placeholder="🔍 Search services / categories…" style={{ flex: 1, padding: '7px 12px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13 }} />
        </div>

        {clientsWithAssignments.length === 0 && (
          <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>No services assigned yet</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>Select a client on the left and assign services from the catalogue</div>
          </div>
        )}

        {clientsWithAssignments.map(client => (
          <ClientCard
            key={client.id}
            client={client}
            assignments={allAssignments.filter(a => a.client_id === client.id)}
            nodes={nodes.filter(n => n.client_id === client.id)}
            searchQ={catSearch}
            onDelete={id => deleteMutation.mutate(id)}
            onUpdatePrice={(id, price) => updatePriceMutation.mutate({ id, price })}
            onColorChange={(clientId, color) => colorMutation.mutate({ clientId, color })}
          />
        ))}
      </div>
    </div>
    </div>
  );
}
